import { supabase } from '../supabase/client';
import { API_CONFIG, getApiUrl } from '../config';

/**
 * Get the base headers for API requests including auth
 * @returns {Object} Headers object
 */
const getBaseHeaders = async () => {
  // Get the session token from Supabase
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Add session ID to headers if provided
 * @param {Object} headers - Base headers
 * @param {string} sessionId - Session ID
 * @returns {Object} Headers with session ID
 */
const addSessionIdHeader = (headers, sessionId) => {
  if (sessionId) {
    return {
      ...headers,
      'X-Session-ID': sessionId
    };
  }
  return headers;
};

/**
 * Gets all available psychometric tests
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const getAllTests = async () => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_ALL_TESTS);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const testsData = await response.json();
    return { data: testsData, error: null };
  } catch (error) {
    console.error('Error getting tests:', error);
    return { data: null, error };
  }
};

/**
 * Start a test session for a specific test
 * @param {number} testId - The ID of the test to start
 * @returns {Promise<Object>} Object containing session data and error properties
 */
export const startTest = async (testId) => {
  try {
    const headers = await getBaseHeaders();
    let apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.START_TEST);
    
    // Replace placeholder with actual testId
    apiUrl = apiUrl.replace('{testId}', testId);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const sessionData = await response.json();
    return { data: sessionData, error: null };
  } catch (error) {
    console.error('Error starting test:', error);
    return { data: null, error };
  }
};

/**
 * Get the next question for a test session
 * @param {string} sessionId - The test session ID
 * @returns {Promise<Object>} Object containing question data and error properties
 */
export const getNextQuestion = async (sessionId) => {
  try {
    const headers = await getBaseHeaders();
    let apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_NEXT_QUESTION);
    
    // Replace placeholder with actual sessionId
    apiUrl = apiUrl.replace('{sessionId}', sessionId);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const questionData = await response.json();
    return { data: questionData, error: null };
  } catch (error) {
    console.error('Error getting next question:', error);
    return { data: null, error };
  }
};

/**
 * Submit an answer for a question in a test session
 * @param {string} sessionId - The test session ID
 * @param {number} questionId - The question ID
 * @param {number} answerValue - The answer value
 * @returns {Promise<Object>} Object containing response data and error properties
 */
export const submitAnswer = async (sessionId, questionId, answerValue) => {
  try {
    const headers = await getBaseHeaders();
    let apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.SUBMIT_ANSWER);
    
    // Replace placeholder with actual sessionId
    apiUrl = apiUrl.replace('{sessionId}', sessionId);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ questionId, answerValue })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    // This endpoint might not return any data
    try {
      const responseData = await response.json();
      return { data: responseData, error: null };
    } catch {
      // Handle empty response
      return { data: { success: true }, error: null };
    }
  } catch (error) {
    console.error('Error submitting answer:', error);
    return { data: null, error };
  }
};

/**
 * Get results for a specific test session
 * @param {string} sessionId - The test session ID
 * @returns {Promise<Object>} Object containing results data and error properties
 */
export const getTestResults = async (sessionId) => {
  try {
    const headers = await getBaseHeaders();
    let apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_TEST_RESULTS);
    
    // Replace placeholder with actual sessionId
    apiUrl = apiUrl.replace('{sessionId}', sessionId);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const resultsData = await response.json();
    return { data: resultsData, error: null };
  } catch (error) {
    console.error('Error getting test results:', error);
    return { data: null, error };
  }
};

/**
 * Get or generate a test report for a specific test session
 * @param {string} sessionId - The test session ID
 * @returns {Promise<Object>} Object containing report data and error properties
 */
export const getTestReport = async (sessionId) => {
  try {
    console.log('Getting test report for session ID:', sessionId);
    if (!sessionId) {
      console.error('No session ID provided');
      return { data: null, error: new Error('No session ID provided') };
    }
    
    const headers = await getBaseHeaders();
    let apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_TEST_REPORT);
    console.log('API URL before replacement:', apiUrl);
    
    // Replace placeholder with actual sessionId
    apiUrl = apiUrl.replace('{sessionId}', sessionId);
    console.log('API URL after replacement:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status}`);
    }
    
    let reportData;
    
    // Try to parse the response as JSON
    try {
      reportData = await response.json();
      console.log('Received report data (JSON):', reportData);
    } catch (parseError) {
      // If it's not JSON, try to get it as text
      console.log('Response is not JSON, trying as text');
      const textData = await response.text();
      console.log('Received report data (text):', textData);
      
      // Create a report object with the text content
      reportData = { 
        reportContent: textData,
        sessionId: sessionId
      };
    }
    
    // Normalize the data structure
    const normalizedData = {
      reportContent: reportData.reportContent || reportData.content || reportData.report || reportData.text || 
                     (reportData.data ? (reportData.data.reportContent || reportData.data.content || reportData.data.text) : null) ||
                     (typeof reportData === 'string' ? reportData : null),
      sessionId: sessionId,
      testId: reportData.testId || null,
      testName: reportData.testName || null,
      endTime: reportData.endTime || reportData.completedAt || reportData.timestamp || new Date().toISOString()
    };
    
    console.log('Normalized report data:', normalizedData);
    
    return { data: normalizedData, error: null };
  } catch (error) {
    console.error('Error getting test report:', error);
    return { data: null, error };
  }
};

/**
 * Get all completed test results for the current user
 * @returns {Promise<Object>} Object containing all results data and error properties
 */
export const getAllResults = async () => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_ALL_RESULTS);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const allResultsData = await response.json();
    return { data: allResultsData, error: null };
  } catch (error) {
    console.error('Error getting all test results:', error);
    return { data: null, error };
  }
}; 